import { startAuthentication } from '@simplewebauthn/browser';
import { useCallback } from 'react';

import { HttpConfig } from '../../../models/https';
import { authActions } from '../../../redux/slices/authSlice';
import { useAppDisPatch, useAppSelector } from '../../../redux/store';
import { useSend } from '../../useApi';

// Calls that need a passkey check from the last 5 minutes (adding a passkey,
// changing pricing or commission rates). The backend answers 403 "Confirm
// it's you…"; we then check the passkey, store the fresh tokens and try once
// more. `call` is the same request without the retry.
const useFreshPasskey = () => {
  const { loading, send } = useSend();
  const dispatch = useAppDisPatch();
  const authData = useAppSelector((state: any) => state.auth.authData);

  const call = useCallback(
    <T,>(config: HttpConfig) =>
      new Promise<T>((resolve, reject) =>
        send(config, (data: any) => resolve(data as T), (message) => reject(new Error(message)))
      ),
    [send]
  );

  const confirmItsYou = useCallback(async () => {
    const optionsJSON = await call<any>({ url: 'api/v1/admin/passkeys/reauth/options', method: 'post' });
    const credential = await startAuthentication({ optionsJSON });
    const tokens = await call<{ access_token: string; refresh_token: string }>({
      url: 'api/v1/admin/passkeys/reauth/verify',
      method: 'post',
      data: { credential },
    });
    const updated = { ...authData, accessToken: { token: tokens.access_token }, refreshToken: { token: tokens.refresh_token } };
    dispatch(authActions.setAuthentication({ authData: updated, isAuthenticated: true }));
    try {
      localStorage.setItem('authData', JSON.stringify(updated));
    } catch {
      // The session still works for this tab.
    }
  }, [authData, call, dispatch]);

  const fresh = useCallback(
    async <T,>(config: HttpConfig): Promise<T> => {
      try {
        return await call<T>(config);
      } catch (err) {
        if (!/Confirm it/.test((err as Error).message)) throw err;
        await confirmItsYou();
        return call<T>(config);
      }
    },
    [call, confirmItsYou]
  );

  return { loading, call, fresh };
};

export default useFreshPasskey;
